/* eslint-disable react/sort-comp */
import React, { Component } from "react";

import AlgoliaContainer from "core-web/containers/AlgoliaContainer";
import PropTypes from "prop-types";
import getQueryParams from "core-web/utils/getQueryParams";
import { inServer } from "core-web/constants";
import { injectModel } from "core-web/state";
import transformArticle from "core-web/utils/dataTransformers/transformArticle";
import { withRouter } from "react-router-dom";

class ArticleContainer extends Component {
    static propTypes = {
        application: PropTypes.object.isRequired,
        firstPageSize: PropTypes.number,
        history: PropTypes.object.isRequired,
        index: PropTypes.string,
        isSearchPage: PropTypes.bool,
        location: PropTypes.object.isRequired,
        onSearch: PropTypes.func,
        page: PropTypes.number,
        pageSize: PropTypes.number,
        render: PropTypes.func.isRequired,
        renderProps: PropTypes.object,
        search: PropTypes.string,
    };

    static defaultProps = {
        firstPageSize: 12,
        index: "posts",
        isSearchPage: false,
        onSearch: null,
        page: 1,
        pageSize: 12,
        renderProps: {},
        search: "",
    };

    state = this.resetState();

    // @todo: Renders too many times (3)
    shouldComponentUpdate(nextProps, nextState) {
        if (JSON.stringify(nextState) !== JSON.stringify(this.state)) {
            return true;
        }

        const { isSearchPage, location, search } = this.props;

        if (
            isSearchPage &&
            nextProps.isSearchPage &&
            nextProps.search !== search &&
            location.pathname === nextProps.location.pathname
        ) {
            this.url.set();
            return true;
        }

        if (JSON.stringify(nextProps.location.search) !== JSON.stringify(location.search)) {
            this.setState(this.resetState());
        }

        return false;
    }

    resetState() {
        const queries = getQueryParams(inServer ? this.props.location : window.location);
        const page = queries.page || this.props.page;
        delete queries.page;

        const filters = {};
        Object.keys(queries)
            .filter((param) => ["tags", "category"].includes(param))
            .forEach((param) => {
                filters[param] = queries[param].split(",").map((paramValue) => decodeURIComponent(paramValue));
            });

        return {
            page,
            filters,
        };
    }

    getParameters = () => {
        const { index, search, pageSize, firstPageSize, application } = this.props;
        const { filters, page } = this.state;
        const locale = application.locale.toLowerCase();
        const articleFilters = [];
        const parameters = [];
        const offset = page === 1 ? 0 : firstPageSize + pageSize * (page - 2);
        const length = page === 1 ? firstPageSize : pageSize;

        for (const param in filters) {
            const values = filters[param];
            const searchFilters = values.map((value) => `${param}:"${decodeURIComponent(value)}"`);

            articleFilters.push(searchFilters);
        }

        parameters.push({
            indexName: `${index}_${locale || process.env.REACT_APP_DEFAULT_LOCALE}`,
            query: search,
            params: {
                facets: [],
                filters: [["post_type:article"], ["template:default"], ...articleFilters].filter((i) => i.length > 0),
                offset,
                length,
            },
        });

        parameters.push({
            indexName: `${index}_${locale || process.env.REACT_APP_DEFAULT_LOCALE}`,
            query: search,
            params: {
                facets: ["category", "tags"],
                filters: [["post_type:article"], ["template:default"]],
                offset,
                length,
            },
        });

        return parameters;
    };

    getPagination = (currentIndex, posts) => {
        if (!posts) {
            return {
                pages: [],
                previousUrl: null,
                nextUrl: null,
                hasMore: false,
                hits: 0,
                hitsCount: 0,
                page: 0,
                pageCount: 1,
                pageSize: 12,
                offset: 0,
            };
        }
        const { firstPageSize, location } = this.props;
        const query = inServer ? getQueryParams(location) : getQueryParams(window.location);
        delete query.page;

        const offset = posts?.offset ?? 0;
        const length = posts?.length ?? 12;
        const hitsCount = posts?.nbHits ?? 0;
        const pageSize = length;
        const pagination = firstPageSize > pageSize ? Math.floor(offset / length) : Math.ceil(offset / length);
        const pageCount =
            firstPageSize > 0 ? Math.ceil((hitsCount - firstPageSize) / pageSize) + 1 : Math.ceil(hitsCount / pageSize);

        const createUrl = (page) => {
            return `${location.pathname}?${Object.entries({ ...query, page })
                .map((q) => q.join("="))
                .join("&")}`;
        };
        return {
            pages: [...new Array(pageCount)].map((page, index) => ({
                name: index + 1,
                current: currentIndex === index,
                url: createUrl(index + 1),
            })),
            previousUrl: currentIndex !== 0 && createUrl(Math.max(1, currentIndex)),
            nextUrl: currentIndex + 1 !== pageCount && createUrl(currentIndex + 2),
            hasMore: posts?.hasMore ?? false,
            hits: (posts?.hits ?? []).length,
            hitsCount: posts?.nbHits ?? 0,
            page: pagination,
            pageCount,
            pageSize,
            offset,
        };
    };

    responseCallback = (response) => {
        if (!response) {
            return null;
        }
        const { onSearch } = this.props;
        const posts = response?.[0] || {};
        const defaultPosts = response[1] || {};
        const currentPageIndex = posts?.page ?? 0;
        const facets = defaultPosts?.facets || {};
        const filters = {};

        for (const key in facets) {
            filters[key] = Object.keys(facets[key]).map((facet) => {
                const filter = this.state.filters[key] ?? [];
                const selected = filter && filter.indexOf(facet) > -1;
                return {
                    value: facet,
                    selected,
                };
            });
        }

        // Fallback: derive category filters from hits when facets.category is empty
        const transformedHits = (posts.hits || []).map((article) => transformArticle(article));
        if (!filters.category || filters.category.length === 0) {
            const categorySet = new Set();
            transformedHits.forEach((article) => {
                if (article.categories) {
                    article.categories.split(" | ").forEach((cat) => cat.trim() && categorySet.add(cat.trim()));
                }
                if (article.category) {
                    categorySet.add(article.category);
                }
            });
            filters.category = Array.from(categorySet)
                .sort()
                .map((facet) => {
                    const filter = this.state.filters.category ?? [];
                    const selected = filter && filter.indexOf(facet) > -1;
                    return { value: facet, selected };
                });
        }

        const data = {
            // filter,
            hits: transformedHits,
            defaultHitsCount: defaultPosts?.nbHits ?? 0,
            filters,
            handleChange: this.handleChange,
            pagination: this.getPagination(currentPageIndex, posts),
            query: posts.query,
        };

        onSearch && onSearch(data);

        return data;
    };

    url = {
        set: (filters = this.state.filters) => {
            const { history, location } = this.props;

            const params = getQueryParams(window.location);

            delete params.page;

            for (const param in filters) {
                const values = (filters[param] || []).map((value) => encodeURIComponent(value)).join(",");
                if (values.length) {
                    params[param] = values;
                } else {
                    delete params[param];
                }
            }

            const url = `?${Object.entries(params)
                .map((q) => q.join("="))
                .join("&")}`;

            history.replace(`${location.pathname}${url}`);
        },
    };

    handleChange = {
        filter: (key, value) => {
            const { filters } = this.state;
            const newFilters = JSON.parse(JSON.stringify(filters));
            if (newFilters[key]) {
                const isSelected = newFilters[key].indexOf(value);
                if (isSelected === -1) {
                    newFilters[key].push(value);
                } else {
                    newFilters[key].splice(isSelected, 1);
                }
            } else {
                newFilters[key] = [value];
            }
            this.url.set(newFilters);
        },
        clearFilter: (key) => {
            const newFilters = JSON.parse(JSON.stringify(this.state.filters));
            newFilters[key] = [];
            this.url.set(newFilters);
        },
    };

    render() {
        const { render, renderProps } = this.props;
        const parameters = this.getParameters();

        return (
            <AlgoliaContainer
                parameters={parameters}
                responseCallback={this.responseCallback}
                render={({ application, ...rest }) => render({ ...rest, renderProps })}
            />
        );
    }
}

export default withRouter(injectModel("application")(ArticleContainer));
